'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { shuffle } from 'lodash';

import { useConfig } from '@/components/providers/config-provider';
import { readJsonFromSessionStorage, writeJsonToSessionStorage } from '@/lib/storage/safe-storage';
import type { ChatSuggestion } from '@/lib/types/tenant';
import { planWelcomeStarters } from '@/lib/utils/welcome-starters';

/**
 * The welcome starters to show, picked by `planWelcomeStarters`: with the switch on, pinned ones
 * first and the free slots from a session-remembered shuffle of the rest. Null until mount when
 * it will shuffle (the caller shows placeholders).
 */
export function useRandomizedSuggestions({
  suggestions,
  isRotating,
  audience,
}: {
  suggestions: ChatSuggestion[] | null;
  isRotating: boolean;
  audience: string;
}): ChatSuggestion[] | null {
  const { tenantKey, meta } = useConfig();
  const locale = meta?.language ?? 'en';

  // SSR and the hydrating first client render must produce the same order;
  // `shuffle` (Math.random) during render would diverge between the two and
  // throw React #418 (text content mismatch). Defer randomization to after
  // mount, where it only ever updates an already-hydrated tree.
  const [mounted, setMounted] = useState(false);
  // Blocked storage reads back empty: without these copies, a refreshed list would reshuffle in view.
  const ordersByKey = useRef<Record<string, string[]>>({});
  useEffect(() => setMounted(true), []);

  return useMemo(() => {
    if (!suggestions || suggestions.length === 0) return [];
    const { fixed, pool, randomSlots } = planWelcomeStarters({
      starters: suggestions,
      isRotating,
    });
    if (randomSlots === 0) return fixed;
    // Null until mount: painting the unshuffled order first visibly swaps the tiles a frame later.
    if (!mounted) return null;

    // Stores the whole shuffled pool's ids, not the visible slice, so a slot freed mid-session (a
    // campaign ending) refills from the same order, and an edited starter keeps its place with its
    // new text. Reconciled against what the server still ships: drop ids it doesn't, so a closed
    // campaign can't return; append new ones, so the row can't underfill.
    const storageKey = `bazak:suggestions:${tenantKey}:${audience}:${locale}`;
    const stored = readJsonFromSessionStorage<unknown[]>({
      key: storageKey,
      fallback: ordersByKey.current[storageKey] ?? [],
    });
    const shippableById = new Map(pool.map(suggestion => [suggestion.id, suggestion]));
    const remembered = stored.filter(
      (id): id is string => typeof id === 'string' && shippableById.has(id),
    );

    const rememberedIds = new Set(remembered);
    const missing = shuffle(pool.filter(s => !rememberedIds.has(s.id))).map(s => s.id);
    const order = [...remembered, ...missing];
    ordersByKey.current[storageKey] = order;
    if (missing.length > 0) writeJsonToSessionStorage({ key: storageKey, value: order });

    const shuffled = order.slice(0, randomSlots).flatMap(id => shippableById.get(id) ?? []);
    return [...fixed, ...shuffled];
  }, [suggestions, isRotating, audience, tenantKey, locale, mounted]);
}
